import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";

const AnimatedSection = ({ children, className = "", style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) => {
  const [ref, isVisible] = useScrollAnimation<HTMLElement>();
  return (
    <section
      ref={ref}
      style={style}
      className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      {children}
    </section>
  );
};

const StoryOfQueebiLand = () => {
  const { t } = useTranslation();
  const { prefix } = useLocale();

  return (
    <>
      <PageHead
        title={t("story.seoTitle")}
        description={t("story.seoDesc")}
        canonical="https://queebi.com/story-of-queebi-land"
        image="/images/og/story-1200x630.jpg"
        imageAlt="The story of Queebi Land — where six emotions found their home"
        keywords="Queebi Land story, emotional learning, children, feelings, characters, origin story, play-based learning"
      />

      <section className="bg-queebi-teal/30 py-20 md:py-32">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold font-heading mb-6">{t("story.title")}</h1>
              <hr className="border-accent w-20 border-t-2 mb-6" />
              <p className="text-xl md:text-2xl font-semibold text-foreground mb-6">{t("story.intro")}</p>
              <p className="text-muted-foreground text-lg">{t("story.introDesc")}</p>
            </div>
            <div className="flex-1">
              <img src="/images/pages/story-earth.png" alt="Queebi animals on earth" className="w-full max-w-lg mx-auto" width={600} height={600} loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <AnimatedSection className="bg-cover bg-center py-20 md:py-32" style={{ backgroundImage: `url(/images/brand/bg-pattern.png)` }}>
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-8">{t("story.childSizedTitle")}</h2>
          <p className="text-muted-foreground text-lg mb-6">{t("story.childSizedP1")}</p>
          <p className="text-muted-foreground text-lg">{t("story.childSizedP2")}</p>
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-card py-20 md:py-32">
        <div className="container max-w-5xl text-center">
          <img src="/images/pages/story-companions.png" alt="Queebi Friends as companions" className="mx-auto w-full max-w-2xl mb-10 rounded-xl" width={800} height={500} loading="lazy" />
          <blockquote className="text-xl md:text-2xl italic text-muted-foreground mb-10 max-w-3xl mx-auto">
            {t("story.companionsQuote")}
          </blockquote>
          <Button asChild className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
            <Link to={`${prefix}/queebi-land-characters`} onClick={() => trackCTAClick('story_meet_friends')}>{t("story.meetFriends")}</Link>
          </Button>
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-background py-20 md:py-32">
        <div className="container max-w-5xl">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <img src="/images/pages/story-grass.png" alt="Queebi friends on grass" className="w-full rounded-xl" width={600} height={420} loading="lazy" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("story.returnTitle")}</h2>
              <p className="text-muted-foreground text-lg mb-4">{t("story.returnP1")}</p>
              <p className="text-muted-foreground text-lg italic">{t("story.returnP2")}</p>
            </div>
          </div>

          <div className="mt-20 text-center">
            <blockquote className="text-xl md:text-2xl font-semibold text-foreground max-w-3xl mx-auto leading-snug">
              {t("story.finalQuote")}
            </blockquote>
          </div>
        </div>
      </AnimatedSection>
    </>
  );
};

export default StoryOfQueebiLand;
